import {
  Pressable,
  type PressableProps,
  type StyleProp,
  type View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

type RowType = 'card';

export interface PressableRowProps
  extends Omit<
    PressableProps,
    'accessibilityRole' | 'children' | 'disabled' | 'ref' | 'style'
  > {
  children: React.ReactNode;
  disabled?: boolean;
  flex?: boolean;
  ref?: React.Ref<View>;
  rounded?: boolean;
  spaced?: boolean;
  style?: StyleProp<ViewStyle>;
  type?: RowType;
}

export default function PressableRow({
  children,
  type,
  ref,
  flex,
  spaced,
  rounded,
  disabled,
  onPress,
  style: extraStyles,
  ...rest
}: PressableRowProps) {
  style.useVariants({
    type,
    flex,
    spaced,
    rounded,
    disabled,
  });

  return (
    <Pressable
      {...rest}
      disabled={disabled}
      onPress={disabled ? undefined : onPress}
      style={({ pressed }) => [
        style.container,
        extraStyles,
        pressed && style.active,
      ]}
      ref={ref}
    >
      {children}
    </Pressable>
  );
}

const style = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.size(4),
    paddingVertical: theme.size(3),
    backgroundColor: 'transparent',
    alignItems: 'center',
    gap: theme.size(3),
    flexDirection: 'row',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
    variants: {
      disabled: {
        true: {
          opacity: 0.5,
          pointerEvents: 'none',
          _web: {
            cursor: 'not-allowed',
          },
        },
      },
      type: {
        card: {
          borderWidth: theme.size(0.5),
          borderColor: theme.colors.borderMuted,
          borderRadius: theme.borderRadius.xl,
          maxWidth: theme.size(80),
          alignSelf: 'flex-start',
        },
        default: {
          borderWidth: 0,
          borderColor: 'transparent',
          borderRadius: 0,
          maxWidth: '100%',
          alignSelf: 'stretch',
        },
      },
      flex: {
        true: {
          flex: 1,
        },
        default: {},
      },
      spaced: {
        true: {
          justifyContent: 'space-between',
        },
        default: {},
      },
      rounded: {
        true: {
          borderRadius: theme.borderRadius.xl,
        },
        default: {},
      },
    },
  },
  active: {
    backgroundColor: theme.colors.surfaceSecondary,
  },
}));
